/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { Localization } from 'connex-cds';
import { get } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import { useGetCompanyAdmin } from '../../../../../../query-hooks/company';
import { useTemplatesSetup } from '../../MasterDataProvider';
import { useResolvedTicketTimeZone } from '../../useResolvedTicketTimeZone';
import processTemplate from './process';
import { suppressEmpty } from './suppressEmpty';

export const TEMPLATES = {
  MATERIAL_SUMMARY: 'materialSummary',
  MATERIAL_INFORMATION: 'materialInfo',
  GENERAL_DELIVERY_INFORMATION: 'generalDeliveryInfo',
};

export const useTemplate = ({ context, templateName, shouldSuppressEmpty = true }) => {
  const intl = useIntl();
  const translateMessage = Localization.useTranslateMessage();
  const { userLocale } = Localization.useLocalizationContext() ?? {};
  const timeZone = useResolvedTicketTimeZone();
  const { data: companyAdmin } = useGetCompanyAdmin();

  const dateTimeSettings = companyAdmin?.dateTimeSettings ?? {};
  const { dateFormat, use24HourTimeFormat } = dateTimeSettings;

  const templatesSetup = useTemplatesSetup();

  const templateString = React.useMemo(() => {
    if (templatesSetup?.data) {
      return get(
        templatesSetup?.data,
        [templateName, userLocale],
        templatesSetup?.data?.[templateName]?.['en-US'] || 'No Template'
      );
    }
  }, [templateName, templatesSetup?.data, userLocale]);

  return React.useMemo(() => {
    const processedTemplate = processTemplate({
      context,
      templateString,
      util: {
        translateMessage,
        intl,
        timeZone,
        userLocale,
        dateFormat,
        use24HourTimeFormat,
      },
    });

    return shouldSuppressEmpty ? suppressEmpty(processedTemplate) : processedTemplate;
  }, [
    context,
    dateFormat,
    intl,
    shouldSuppressEmpty,
    templateString,
    translateMessage,
    timeZone,
    use24HourTimeFormat,
    userLocale,
  ]);
};
